import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Send } from 'lucide-react';
import { formatTime } from '../utils';

const ALIASES = ["Anonymous", "Student 🤓", "Alumni", "Senior", "Professor", "Freshman"];
const getRandomAlias = () => ALIASES[Math.floor(Math.random() * ALIASES.length)];

export default function CommentSection({ comments = [], postId }) {
  const [newComment, setNewComment] = useState('');
  const [commentList, setCommentList] = useState(comments);
  const { addComment, token } = useContext(AppContext);
  const navigate = useNavigate();

  const handleComment = (e) => {
    if (e) e.preventDefault();
    if (!token) {
        navigate('/login');
        return;
    }
    
    if (!newComment.trim()) return;
    
    // Optimistic UI update
    setCommentList([
      ...commentList,
      {
        id: Date.now().toString(),
        text: newComment,
        author: getRandomAlias(),
        timestamp: new Date().toISOString()
      }
    ]);
    
    // API Call
    if (postId) {
      addComment(postId, newComment);
    }
    
    setNewComment('');
  };

  return (
    <motion.div 
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-900/50"
    >
      <div className="space-y-4 mb-4">
        {commentList.length === 0 ? (
          <p className="text-sm text-slate-400 dark:text-slate-600 text-center py-2">No comments yet. Be the first to reply!</p>
        ) : (
          commentList.map((comment) => (
            <div key={comment.id} className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-900 flex-shrink-0 flex items-center justify-center">
                <span className="text-sm">🥸</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-semibold text-slate-900 dark:text-white">{comment.author}</span>
                  <span className="text-[11px] text-slate-400 font-medium">
                    {formatTime(comment.timestamp)}
                  </span>
                </div>
                <p className="text-[15px] text-slate-700 dark:text-slate-300 leading-snug">{comment.text}</p>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="relative">
        <input 
          type="text" 
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder={token ? "Reply anonymously..." : "Login to reply..."}
          className="w-full bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-800 rounded-full py-2.5 pl-4 pr-12 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 dark:focus:ring-slate-500 transition-all placeholder:text-slate-400"
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleComment();
          }}
        />
        <button 
          onClick={() => handleComment()}
          disabled={!newComment.trim()}
          className="absolute right-1.5 top-1.5 p-1.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 transition-transform"
        >
          <Send className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
}
